// @flow
import {isNode, isEdge} from 'react-flow-renderer';
import dagre from 'dagre';

import type {Element} from '../types';

// Handle names, used to position where on the node the edge leaves / enters
const RIGHT = 'right';
const TOP = 'top';
const LEFT = 'left';
const BOTTOM = 'bottom';

type Point = {|
  x: number,
  y: number,
|};

// dagre library
type DagreEdge = {|
  v: string,
  w: string,
  points: [Point, Point, Point],
|};

// Calculate where the edge should leave & enter the two nodes
// For example, a [N1] --> [N2] has a handle leaving the right side of N1 (source) and entering the left of N2 (target)
// as opposed to this example [N1] <-- [N2] where the handle is leaving the left side of N2 to the right of N1
// react-flow-renderer calls these positions "handles" and they can be placed anywhere in a node
// all our diagrams (except sequence) have the options for left, right, top, and bottom handles

// Sometimes two nodes will have two edges connecting them
// if that's the case and an edge previously exists, we want to draw an edge coming from different handles
// so the two edges (and their potential labels) don't overlap
const getHandlePosition = (
  edge: DagreEdge,
  edgeExists: boolean,
): [string, string] => {
  /* eslint-disable no-unused-vars */
  const [source, middle, target] = edge.points;
  const offsetX = target.x - source.x;
  const offsetY = target.y - source.y;

  if (offsetX > 0) {
    // Then the target node is positioned to the right of the source
    if (edgeExists) {
      return offsetY > 0 ? [RIGHT, TOP] : [RIGHT, BOTTOM];
    }
    return [RIGHT, LEFT];
  } else if (offsetX < 0) {
    // Then the target node is positioned to the left of the source
    if (edgeExists) {
      return offsetY > 0 ? [LEFT, TOP] : [LEFT, BOTTOM];
    }
    return [LEFT, RIGHT];
  } else if (offsetY > 0) {
    // Then the source node is above the target node
    return edgeExists ? [RIGHT, RIGHT] : [TOP, BOTTOM];
  } else {
    return edgeExists ? [LEFT, LEFT] : [BOTTOM, TOP];
  }
};

export const positionElements = (
  nodes: Array<any>,
  elements: Array<Element>,
): Array<Element> => {
  if (!nodes.length) {
    return [];
  }
  const g = new dagre.graphlib.Graph({multigraph: true});
  // Set default labels for graph
  g.setGraph({rankdir: 'LR', ranksep: 200, nodesep: 200});
  g.setDefaultEdgeLabel(function() {
    return {};
  });

  nodes.forEach((n) => {
    g.setNode(n.id, {
      label: n.id,
      width: n.__rf.width,
      height: n.__rf.height,
    });
  });

  elements.forEach((e) => {
    if (isEdge(e) && e.source && e.target) {
      g.setEdge(e.source, e.target);
    }
  });

  dagre.layout(g);

  // Keep track of the draw edges so we know when we have overlapping edges
  const drawnEdges = [];

  return elements
    .map((e) => {
      if (isNode(e) && e.position) {
        const node = g.node(e.id);
        return {
          ...e,
          position: {
            x: e.position.x || node.x,
            y: e.position.y || node.y,
          },
        };
      } else if (isEdge(e) && e.source && e.target) {
        const edge = g.edge({v: e.source, w: e.target});
        drawnEdges.push(`${e.source}-${e.target}`);

        // Compute default edge handles
        const [sourceHandle, targetHandle] = getHandlePosition(
          edge,
          drawnEdges.includes(`${e.target}-${e.source}`),
        );

        // Some nodes (like entity nodes) can override edge handles to
        // point to a specific location in the node, so only use computed
        // handles if a handle hasn't been specified
        return {
          ...e,
          sourceHandle: e.sourceHandle || sourceHandle,
          targetHandle: e.targetHandle || targetHandle,
        };
      } else {
        // Flow made me do this, grrrrrr
        return null;
      }
    })
    .filter(Boolean);
};
